import React, { useState, useEffect, useRef } from "react";
import { Calendar, Upload, X, Search, Loader } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { set } from "date-fns";

export default function RefundForm({ onClose }) {
  const [oldTransaction, setOldTransaction] = useState("");
  const [selectedTransactionAmount, setSelectedTransactionAmount] =
    useState("");
  const [reasonForRefund, setReasonForRefund] = useState("");
  const [refundStatus, setRefundStatus] = useState("Full Refund");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethod, setRefundMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [selectedTransactionCatagory, setSelectedTransactionCatagory] =
    useState(null);
  const [transactionDate, setTransactionDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [refundTransactionCatagory, setRefundTransactionCatagory] =
    useState(null);
  const [otherTransactionCatagory, setOtherTransactionCatagory] =
    useState(null);
  const [materialTransactionCatagory, setMaterialTransactionCatagory] =
    useState(null);
  const [workerTransactionCatagory, setWorkerTransactionCatagory] =
    useState(null);
  const [receivedTransactionCatagory, setReceivedTransactionCatagory] =
    useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    // Set refund amount to full amount when Full Refund is selected
    if (refundStatus === "Full Refund" && selectedTransactionAmount) {
      setRefundAmount(selectedTransactionAmount.toString());
    }
  }, [refundStatus, selectedTransactionAmount]);

  const fetchTransactions = async () => {
    try {
      const site = localStorage.getItem("site");
      const accessToken = localStorage.getItem("access");
      const formData = new FormData();
      formData.append("sites", site);
      const response = await fetch(
        `${BASE_URL}/finance/get-all-transactions-of-a-site/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );
      const data = await response.json();
      if (data.status) {
        const validTransactions = data.data.filter((tr) => !tr.is_refunded);
        setTransactions(validTransactions);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const handleTransactionSelect = (transaction) => {
    const amount = transaction.amount || transaction.paid_amount;
    setOldTransaction(
      `₹${amount} - ${transaction.transaction_date || transaction?.date}`
    );
    setSelectedTransactionAmount(amount);
    setSelectedTransactionId(transaction.id);
    setSelectedTransactionCatagory(transaction.transaction_catagory_id);
    // Set initial refund amount to full amount
    setRefundAmount(amount.toString());
    setIsModalOpen(false);
  };

  const handleRefundAmountChange = (e) => {
    const value = e.target.value;
    // Only allow changes if Partial Refund is selected
    if (refundStatus === "Partial Refund") {
      // Ensure the entered amount doesn't exceed the selected transaction amount
      if (
        !value ||
        parseFloat(value) <= parseFloat(selectedTransactionAmount)
      ) {
        setRefundAmount(value);
      }
    }
  };
  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "Refund"
          );
          if (purchaseCatagory) {
            setRefundTransactionCatagory(purchaseCatagory.id);
          }
          const otherCatagory = data.find(
            (cat) => cat.catagory_name === "General Transaction"
          );
          if (otherCatagory) {
            setOtherTransactionCatagory(otherCatagory.id);
          }
          const materialCatagory = data.find(
            (cat) => cat.catagory_name === "Material Transaction"
          );
          if (materialCatagory) {
            setMaterialTransactionCatagory(materialCatagory.id);
          }
          const workerCatagory = data.find(
            (cat) => cat.catagory_name === "Worker Transaction"
          );
          if (workerCatagory) {
            setWorkerTransactionCatagory(workerCatagory.id);
          }
          const receivedCatagory = data.find(
            (cat) => cat.catagory_name === "Received"
          );
          if (receivedCatagory) {
            setReceivedTransactionCatagory(receivedCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);
  const handleSave = async () => {
    if (!selectedTransactionId || !refundAmount) return;

    setIsSaveDisabled(true);
    setIsLoading(true);

    const site = localStorage.getItem("site");
    const currentDate = new Date();
    const formattedDate = `${transactionDate.getFullYear()}-${(
      currentDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${currentDate.getDate().toString().padStart(2, "0")}`;

    const refundData = {
      transaction_type: "Refund",
      transaction_date: formattedDate,
      original_transaction_id: selectedTransactionId,
      original_transaction_catagory_id:
        selectedTransactionCatagory || otherTransactionCatagory,
      amount: parseInt(refundAmount),
      transaction_method: refundMethod,
      transaction_status: refundStatus,
      comments: remarks,
      receipt_url: "",
      transaction_catagory_id: refundTransactionCatagory,
      site: parseInt(site),
    };

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/create-general-transaction/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(refundData),
        }
      );
      const data = await response.json();
      if (data.status) {
        onClose();
      } else {
        setIsSaveDisabled(false);
      }
    } catch (error) {
      console.error("Error creating refund:", error);
      setIsSaveDisabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNowClick = () => {
    setTransactionDate(new Date());
    setShowDatePicker(false);
  };

  const handleDateChange = (e) => {
    const selectedDate = new Date(e.target.value);

    setTransactionDate(new Date(e.target.value));
    setShowDatePicker(false);
  };

  const handlePickOlderDateClick = () => {
    setShowDatePicker(true);
  };

  const filteredTransactions = transactions.filter((transaction) => {
    if (!transaction) return false;

    const amountString = transaction.amount?.toString() || "";
    const dateString = transaction.transaction_date || "";

    return amountString.includes(searchTerm) || dateString.includes(searchTerm);
  });

  return (
    <>
      <div className="w-full p-6 bg-gray-50">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 bg-white p-4 rounded-lg shadow-sm">
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Refund</h1>
          <div className="w-5" />
        </div>

        {/* Main Form */}
        <div className="grid grid-cols-2 gap-4">
          {/* First Box: Old Transaction Details and Reason for Refund */}
          <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Old Transaction Details
              </label>
              <button
                className="w-full px-4 py-2 bg-gray-100 rounded-md text-left"
                onClick={() => {
                  setIsModalOpen(true);
                  fetchTransactions();
                }}
              >
                {oldTransaction || "Select Old Transaction"}
              </button>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Reason for Refund
              </label>
              <input
                type="text"
                className="w-full p-3 bg-white rounded-lg border border-gray-200"
                placeholder="Type"
                value={reasonForRefund}
                onChange={(e) => setReasonForRefund(e.target.value)}
              />
            </div>
          </div>

          {/* Second Box: Refund Date */}
          <div className="bg-white p-4 rounded-lg shadow-sm relative">
            <label className="block text-sm text-gray-600 mb-1">
              Refund Date
            </label>
            <div className="flex gap-2">
              <button
                className={`px-4 py-2 rounded-full ${
                  new Date().toDateString() === transactionDate.toDateString()
                    ? "bg-purple-100 text-purple-600"
                    : "bg-gray-100 text-gray-600"
                } text-sm`}
                onClick={handleNowClick}
              >
                Now
              </button>
              <button
                className={`px-4 py-2 rounded-full ${
                  new Date().toDateString() === transactionDate.toDateString()
                    ? "bg-gray-100 text-gray-600"
                    : "bg-purple-100 text-purple-600"
                } text-sm flex items-center gap-2`}
                onClick={handlePickOlderDateClick}
              >
                Pick an older date
                <Calendar className="h-4 w-4" />
              </button>
            </div>

            {showDatePicker && (
              <div className="absolute z-10 bg-white shadow-lg rounded-lg p-4 mt-2 left-0 right-0">
                <input
                  type="date"
                  className="w-full p-2 border rounded-md"
                  value={transactionDate.toISOString().split("T")[0]}
                  onChange={handleDateChange}
                  max={new Date().toISOString().split("T")[0]}
                />
                <button
                  className="w-full mt-2 bg-purple-100 text-purple-600 rounded-md p-2"
                  onClick={() => setShowDatePicker(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>

          {/* Third Box: Refund Status, Amount, and Method */}
          <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Refund Status
              </label>
              <div className="flex gap-2">
                {["Full Refund", "Partial Refund"].map((status) => (
                  <button
                    key={status}
                    className={`px-4 py-2 rounded-full text-sm ${
                      refundStatus === status
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    onClick={() => setRefundStatus(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                ₹ Refund Amount
              </label>
              <input
                type="number"
                className="w-full p-3 bg-white rounded-lg border border-gray-200"
                placeholder="Enter refund amount"
                value={refundAmount}
                onChange={handleRefundAmountChange}
                disabled={
                  refundStatus === "Full Refund" || !selectedTransactionAmount
                }
              />
              {selectedTransactionAmount - refundAmount < 0 && (
                <p className="text-red-500 text-sm mt-1">
                  Refund amount cannot exceed the original transaction amount.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Refund Method
              </label>
              <div className="flex flex-wrap gap-2">
                {["Cash", "UPI/Bank Transfer", "Cheque"].map((method) => (
                  <button
                    key={method}
                    className={`px-4 py-2 rounded-full text-sm ${
                      refundMethod === method
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    onClick={() => setRefundMethod(method)}
                  >
                    {method}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Fourth Box: Remarks and Payment Slip */}
          <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
            <div>
              <input
                type="text"
                className="w-full p-2 bg-transparent border-b border-gray-300"
                placeholder="Remarks / Comments (Optional)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          className={`w-full mt-6 py-3 rounded-md font-medium transition duration-300 flex items-center justify-center gap-2 ${
            isSaveDisabled || isLoading
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-[#3B0069] text-white hover:bg-purple-700"
          }`}
          onClick={handleSave}
          disabled={isSaveDisabled || isLoading}
        >
          {isLoading ? (
            <>
              <Loader className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : isSaveDisabled ? (
            "Saved"
          ) : (
            "Save"
          )}
        </button>
      </div>

      {/* Custom Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            className="bg-white rounded-lg w-[600px] max-h-[80vh] overflow-hidden"
          >
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Select Transaction</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by amount or date..."
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                {filteredTransactions
                  .filter(
                    (tx) =>
                      ![
                        materialTransactionCatagory,
                        workerTransactionCatagory,
                        receivedTransactionCatagory,
                        refundTransactionCatagory,
                      ].includes(tx.transaction_catagory_id)
                  )
                  .map((transaction) => (
                    <div
                      key={transaction.id}
                      className="p-3 border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleTransactionSelect(transaction)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="w-[25%]">
                          <p className="font-medium">
                            ₹{transaction.amount ?? transaction.paid_amount}
                          </p>
                          <p className="text-sm text-gray-600">
                            {transaction.transaction_date}
                          </p>
                        </div>
                        <div className="text-left w-[50%]">
                          <p className="text-sm text-gray-600">
                            {transaction.transaction_catagory_id ===
                            refundTransactionCatagory
                              ? "Refund"
                              : transaction.transaction_catagory_id ===
                                workerTransactionCatagory
                              ? `Workers (${transaction.worker_name})`
                              : transaction.transaction_catagory_id ===
                                otherTransactionCatagory
                              ? transaction.transaction_type
                              : transaction.transaction_catagory_id ===
                                materialTransactionCatagory
                              ? "Material Purchase"
                              : transaction.transaction_catagory_id ===
                                receivedTransactionCatagory
                              ? "Received"
                              : "Others"}
                          </p>
                        </div>
                        <div className="text-right w-[25%]">
                          <p className="text-sm text-gray-600">
                            {transaction.transaction_method ??
                              transaction.payment_method}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
